/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import BrandName from '../components/common/BrandName'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { meAPI, publicInvitesAPI } from '../api/crm'
import { loginUser, signOut } from '../store/slices/authSlice'
import SsoButtons from '../components/common/SsoButtons'
import { selectTenant } from '../store/slices/tenantSlice'
import { getErrorMessage } from '../utils/apiErrors'
import { formatDate } from '../utils/helpers'
import Loading from '../components/common/Loading'

const ROLE_LABEL = { TENANT_ADMIN: 'an admin', MEMBER: 'a member' }

const Frame = ({ children }) => (
  <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
    <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 420 }}>
      <BrandName sx={{ mb: 2 }} />
      {children}
    </Paper>
  </Box>
)

/**
 * Public landing page of an invite link. New people set a password; people with an account
 * sign in (as the invited email) and accept.
 */
const InvitePage = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useSelector((state) => state.auth)
  const { data: invite, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => publicInvitesAPI.preview(token),
    retry: false,
  })

  const enterWorkspace = (accepted) => {
    dispatch(selectTenant({ tenantId: accepted.tenantId, name: accepted.tenantName, role: accepted.role, status: 'ACTIVE' }))
    queryClient.removeQueries()
    navigate('/', { replace: true })
  }

  if (isLoading) return <Loading fullScreen />
  if (error) {
    return (
      <Frame>
        <Alert severity="warning" sx={{ mb: 2 }}>{getErrorMessage(error, 'This invite link is not valid')}</Alert>
        <Button variant="contained" onClick={() => navigate('/login')}>Go to sign in</Button>
      </Frame>
    )
  }

  const signedInAsOther = isAuthenticated && user?.email?.toLowerCase() !== invite.email.toLowerCase()

  return (
    <Frame>
      <Typography variant="h5">Join {invite.tenantName}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        You’ve been invited as {ROLE_LABEL[invite.role] || 'a member'}. This link expires {formatDate(invite.expiresAt)}.
      </Typography>

      {signedInAsOther ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            You’re signed in as {user.email}, but this invite is for {invite.email}.
          </Alert>
          <Button variant="contained" onClick={() => { dispatch(signOut()).then(() => queryClient.clear()) }}>
            Sign out and continue
          </Button>
        </>
      ) : invite.accountExists ? (
        <ExistingAccount invite={invite} token={token} isAuthenticated={isAuthenticated} onAccepted={enterWorkspace} />
      ) : (
        <NewAccount invite={invite} token={token} onAccepted={enterWorkspace} />
      )}
    </Frame>
  )
}

const ExistingAccount = ({ invite, token, isAuthenticated, onAccepted }) => {
  const navigate = useNavigate()
  const accept = useMutation({ mutationFn: () => meAPI.acceptInvite(token), onSuccess: onAccepted })

  if (!isAuthenticated) {
    const next = encodeURIComponent(`/invite/${token}`)
    return (
      <>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {invite.email} already has an account. Sign in to accept.
        </Typography>
        <Button variant="contained" fullWidth onClick={() => navigate(`/login?next=${next}&email=${encodeURIComponent(invite.email)}`)}>
          Sign in to join
        </Button>
        <SsoButtons next={`/invite/${token}`} />
      </>
    )
  }
  return (
    <>
      {accept.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(accept.error)}</Alert>}
      <Button variant="contained" fullWidth onClick={() => accept.mutate()} disabled={accept.isPending}>
        {accept.isPending ? 'Joining…' : `Join ${invite.tenantName}`}
      </Button>
    </>
  )
}

const NewAccount = ({ invite, token, onAccepted }) => {
  const dispatch = useDispatch()
  const [name, setName] = useState(invite.name || '')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [formError, setFormError] = useState(null)

  const accept = useMutation({
    mutationFn: async () => {
      const accepted = await publicInvitesAPI.accept(token, password, name.trim())
      // the account now exists: sign straight in with the password just chosen
      const login = await dispatch(loginUser({ email: invite.email, password }))
      if (!loginUser.fulfilled.match(login)) throw new Error('Account created. Please sign in.')
      return accepted
    },
    onSuccess: onAccepted,
  })

  const submit = (e) => {
    e.preventDefault()
    if (password.length < 8) return setFormError('Use at least 8 characters')
    if (password !== confirm) return setFormError('Passwords don’t match')
    setFormError(null)
    accept.mutate()
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={2}>
        {(formError || accept.error) && <Alert severity="error">{formError || getErrorMessage(accept.error)}</Alert>}
        <TextField label="Email" value={invite.email} InputProps={{ readOnly: true }} autoComplete="username" />
        <TextField label="Your name" required autoFocus autoComplete="name" value={name} onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 100 }} helperText="Your teammates see this instead of your email" />
        <TextField label="Choose a password" type="password" required autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} helperText="At least 8 characters" />
        <TextField label="Confirm password" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button type="submit" variant="contained" size="medium" disabled={accept.isPending}>
          {accept.isPending ? 'Creating account…' : 'Create account and join'}
        </Button>
      </Stack>
      {/* with a provider: sign in as invite.email there, then join on this page */}
      <SsoButtons next={`/invite/${token}`} dividerLabel={`or use your ${invite.email} account`} />
    </Box>
  )
}

export default InvitePage
